import { AuthProvider, SocialAuthConfig, EmailPasswordAuthProvider } from './types';
import { GoogleAuthService } from './googleAuth';
import { FacebookAuthService } from './facebookAuth';
import { InstagramAuthService } from './instagramAuth';
import { EmailPasswordAuthService } from './emailPasswordAuth';

export interface AuthProviderConfig {
  google?: SocialAuthConfig;
  facebook?: SocialAuthConfig;
  instagram?: SocialAuthConfig;
  emailPassword?: { enabled: boolean };
}

export class AuthManager {
  private providers: Map<string, AuthProvider> = new Map();
  
  private emailPasswordProvider: EmailPasswordAuthProvider | null = null;

  private currentProvider: AuthProvider | null = null;
  
  private config: AuthProviderConfig;

  constructor(config: AuthProviderConfig = {}) {
    this.config = config;
    this.initializeProviders();
  }

  private initializeProviders() {
    // Initialize Google Auth - Firebase handles OAuth internally, no appId needed
    if (this.config.google) {
      this.registerProvider('google', new GoogleAuthService(this.config.google));
    }

    // Initialize Facebook Auth
    if (this.config.facebook && this.config.facebook.appId) {
      this.registerProvider('facebook', new FacebookAuthService(this.config.facebook));
    }

    // Initialize Instagram Auth
    if (this.config.instagram && this.config.instagram.appId) {
      this.registerProvider('instagram', new InstagramAuthService(this.config.instagram));
    }

    // Initialize Email/Password Auth
    if (this.config.emailPassword && this.config.emailPassword.enabled) {
      this.emailPasswordProvider = new EmailPasswordAuthService();
    }
  }

  registerProvider(name: string, provider: AuthProvider) {
    this.providers.set(name, provider);
  }

  getProvider(name: string): AuthProvider | null {
    return this.providers.get(name) || null;
  }

  setCurrentProvider(name: string) {
    const provider = this.providers.get(name);
    if (provider) {
      this.currentProvider = provider;
    }
  }

  getCurrentProvider(): AuthProvider | null {
    return this.currentProvider;
  }

  getAvailableProviders(): string[] {
    return Array.from(this.providers.keys());
  }

  isProviderAvailable(name: string): boolean {
    if (name === 'emailPassword') {
      return this.emailPasswordProvider !== null;
    }
    return this.providers.has(name);
  }

  getEmailPasswordProvider(): EmailPasswordAuthProvider | null {
    return this.emailPasswordProvider;
  }
} 